import React, { useState, useEffect } from 'react';

const WaterFillingChart = ({
  percentage = 35,
  title = "Recycling Rate",
  description = "Lithium is rated 90 out of 100 for its essentiality. It's critical for all modern electronics and the electric vehicle industry",
  showLabels = true
}) => {
  const [displayPercentage, setDisplayPercentage] = useState(0);
  const [animatedHeight, setAnimatedHeight] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [previousPercentage, setPreviousPercentage] = useState(0);

  // Animate only when percentage increases
  useEffect(() => {
    if (percentage > previousPercentage) {
      setIsAnimating(true);
      const startValue = previousPercentage;
      const endValue = percentage;
      const duration = 1200;
      const startTime = Date.now();

      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const easeOutQuart = 1 - Math.pow(1 - progress, 4);
        const currentValue = startValue + (endValue - startValue) * easeOutQuart;

        setDisplayPercentage(currentValue);
        setAnimatedHeight(currentValue);

        if (progress < 1) {
          requestAnimationFrame(animate);
        } else {
          setIsAnimating(false);
        }
      };

      animate();
    } else {
      // If not increasing, just set the values directly
      setDisplayPercentage(percentage);
      setAnimatedHeight(percentage);
    }

    setPreviousPercentage(percentage);
  }, [percentage, previousPercentage]);

  const size = 150;
  const centerX = size / 2;
  const centerY = size / 2;
  const radius = 60;

  // Calculate water level based on percentage
  const waterHeight = (animatedHeight / 100) * (radius * 2);
  const waterY = centerY + radius - waterHeight;

  // Simple wave path for the water surface
  const createWavePath = (time = 0) => {
    const waveHeight = 3;
    const waveLength = 25;
    const phase = time * 0.002;

    let path = `M ${centerX - radius} ${waterY}`;

    // Create subtle wave using sine function
    for (let x = -radius; x <= radius; x += 2) {
      const y = waterY + Math.sin((x / waveLength + phase) * Math.PI * 2) * waveHeight;
      path += ` L ${centerX + x} ${y}`;
    }

    // Close the path to fill the bottom
    path += ` L ${centerX + radius} ${centerY + radius}`;
    path += ` L ${centerX - radius} ${centerY + radius}`;
    path += ` Z`;

    return path;
  };

  const [wavePath, setWavePath] = useState(createWavePath());

  // Animate waves only during filling
  useEffect(() => {
    let animationFrame;

    if (isAnimating && animatedHeight > 0) {
      let startTime = Date.now();
      const animateWaves = () => {
        const currentTime = Date.now() - startTime;
        setWavePath(createWavePath(currentTime));
        animationFrame = requestAnimationFrame(animateWaves);
      };
      animateWaves();
    } else if (animatedHeight > 0) {
      // Set static wave when not animating
      setWavePath(createWavePath(0));
    }

    return () => {
      if (animationFrame) {
        cancelAnimationFrame(animationFrame);
      }
    };
  }, [animatedHeight, waterY, isAnimating]);

  return (
    <div className="water-filling-chart-container">
      <div className="water-filling-chart-wrapper">
        <div className="water-filling-chart-svg-container">
          <svg width={size} height={size} className="water-filling-chart-svg">
            <defs>
              {/* Blue water gradient */}
              <linearGradient id="waterGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                <stop offset="0%" stopColor="#60A5FA" />
                <stop offset="100%" stopColor="#3B82F6" />
              </linearGradient>

              {/* Clip path for circular container */}
              <clipPath id="circleClip">
                <circle cx={centerX} cy={centerY} r={radius} />
              </clipPath>
            </defs>

            {/* Main blue circle background */}
            <circle
              cx={centerX}
              cy={centerY}
              r={radius}
              fill="transparent"
              stroke="#60A5FA"
              strokeWidth="3"
            />

            {/* Water fill with subtle wave */}
            <g clipPath="url(#circleClip)">
              {animatedHeight > 0 && (
                <path
                  d={wavePath}
                  fill="url(#waterGradient)"
                  className="water-fill"
                />
              )}
            </g>
          </svg>

          <div className="water-filling-center-text">
            <div className="water-filling-percentage">
              {Math.round(displayPercentage)}%
            </div>
          </div>
        </div>
      </div>

      {showLabels && (
        <>
          <h2 className="water-filling-title">
            {title}
          </h2>

          <p className="water-filling-description">
            {description}
          </p>
        </>
      )}
    </div>
  );
};

export default WaterFillingChart;